import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import axios from "axios";
import { API } from "@/config";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, ChevronRight, Check, AlertCircle, Loader2 } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";
import { db } from "../db";
import { ReticleSignal, signal } from "@/reticle";


import PropTypes from 'prop-types';

export default function Clinics({ token, onLogout }) {
    const navigate = useNavigate();
    const doctorId = localStorage.getItem('user_id');
    const [showAddModal, setShowAddModal] = useState(false);
    const [newClinic, setNewClinic] = useState({ name: '', address: '', phone: '', email: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isAvailable, setIsAvailable] = useState(null); // null=unchecked, true=available, false=exists
    const [isChecking, setIsChecking] = useState(false);

    // Reset availability when modal closes
    useEffect(() => {
        if (!showAddModal) {
            setIsAvailable(null);
            setIsChecking(false);
            setNewClinic({ name: '', address: '', phone: '', email: '' });
        }
    }, [showAddModal]);

    // Debounced availability check
    useEffect(() => {
        if (!newClinic.name || !newClinic.phone) {
            setIsAvailable(null);
            return;
        }
        setIsChecking(true);
        const timer = setTimeout(async () => {
            try {
                const res = await axios.get(`${API}/clinics/search`, {
                    params: { name: newClinic.name, phone: newClinic.phone },
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsAvailable(res.data.length === 0);
            } catch (error) {
                console.error('Availability check failed:', error);
                setIsAvailable(null); // Reset on error
            } finally {
                setIsChecking(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [newClinic.name, newClinic.phone, token]);

    // Get clinics from local DB
    const clinics = useLiveQuery(async () => {
        const memberships = await db.clinic_members
            .where('user_id').equals(doctorId)
            .filter(m => m.is_active !== false)
            .toArray();

        const clinicIds = memberships.map(m => m.clinic_id);
        const clinicList = await db.clinics.where('id').anyOf(clinicIds).toArray();

        // Enrich with role info
        return clinicList.map(clinic => {
            const membership = memberships.find(m => m.clinic_id === clinic.id);
            return { ...clinic, my_role: membership?.role };
        });
    }, [doctorId]);

    const handleCreateClinic = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await axios.post(`${API}/clinics`, newClinic, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Save to local DB
            await db.clinics.put({ ...response.data, _synced: true });

            // Create membership record
            const memberData = {
                id: `mem_${response.data.id}_${doctorId}`,
                clinic_id: response.data.id,
                user_id: doctorId,
                role: 'admin',
                is_active: true,
                created_at: new Date().toISOString(),
                _synced: true
            };
            await db.clinic_members.put(memberData);
            signal(ReticleSignal.CLINIC_CREATED, {
                clinicId: response.data.id,
                actorId: doctorId,
                role: 'admin'
            });

            setShowAddModal(false);
            setNewClinic({ name: '', address: '', phone: '', email: '' });
            toast.success("Clinic created successfully!");
        } catch (error) {
            console.error(error);
            let errorMessage = "Failed to create clinic";
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'object' && detail.message) {
                    errorMessage = detail.message;
                } else if (typeof detail === 'string') {
                    errorMessage = detail;
                }
            }
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin': return { bg: '#dbeafe', color: '#1e40af' };
            case 'doctor': return { bg: '#d1fae5', color: '#065f46' };
            case 'receptionist': return { bg: '#fef3c7', color: '#92400e' };
            default: return { bg: '#f1f5f9', color: '#475569' };
        }
    };

    return (
        <div className="page-container" data-testid="clinics-page">
            <Sidebar active="clinics" onLogout={onLogout} />
            <div className="main-content">
                <div className="header">
                    <h1>My Clinics</h1>
                    <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                        <DialogTrigger asChild>
                            <Button style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
                                <Plus size={18} style={{ marginRight: '0.5rem' }} />
                                Create Clinic
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Create New Clinic</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateClinic} style={{ marginTop: '1rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div>
                                        <Label htmlFor="name">Clinic Name *</Label>
                                        <Input
                                            id="name"
                                            value={newClinic.name}
                                            onChange={(e) => setNewClinic({ ...newClinic, name: e.target.value })}
                                            placeholder="Enter clinic name"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="address">Address</Label>
                                        <Input
                                            id="address"
                                            value={newClinic.address}
                                            onChange={(e) => setNewClinic({ ...newClinic, address: e.target.value })}
                                            placeholder="Enter address"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="phone">Phone *</Label>
                                        <PhoneInput
                                            id="phone"
                                            value={newClinic.phone}
                                            onChange={(val) => setNewClinic({ ...newClinic, phone: val })}
                                            placeholder="Enter phone number"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={newClinic.email}
                                            onChange={(e) => setNewClinic({ ...newClinic, email: e.target.value })}
                                            placeholder="Enter clinic email"
                                        />
                                    </div>

                                    {/* Availability indicator */}
                                    {(newClinic.name && newClinic.phone) && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.5rem 0.75rem',
                                            borderRadius: '8px',
                                            fontSize: '0.875rem',
                                            background: isChecking ? '#f8fafc' : isAvailable ? '#dcfce7' : '#fef2f2',
                                            color: isChecking ? '#64748b' : isAvailable ? '#166534' : '#dc2626'
                                        }}>
                                            {isChecking ? (
                                                <><Loader2 size={16} className="animate-spin" /> Checking availability...</>
                                            ) : isAvailable ? (
                                                <><Check size={16} /> Available</>
                                            ) : (
                                                <><AlertCircle size={16} /> A clinic with this name and phone already exists</>
                                            )}
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={isLoading || !isAvailable}
                                        style={{ marginTop: '0.5rem' }}
                                    >
                                        {isLoading ? 'Creating...' : 'Create Clinic'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {!clinics || clinics.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                        <Building2 size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
                        <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>No Clinics Yet</h3>
                        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                            Create a clinic or ask an admin to add you
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {clinics.map(clinic => {
                            const roleStyle = getRoleBadgeColor(clinic.my_role);
                            return (
                                <div
                                    key={clinic.id}
                                    className="card clickable"
                                    data-testid="clinic-card"
                                    onClick={() => navigate(`/clinics/${clinic.id}`)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <div style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Building2 size={24} color="white" />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{clinic.name}</div>
                                            {clinic.address && (
                                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{clinic.address}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <span style={{
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '9999px',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            textTransform: 'capitalize',
                                            background: roleStyle.bg,
                                            color: roleStyle.color
                                        }} data-testid="clinic-role">
                                            {clinic.my_role}
                                        </span>
                                        <ChevronRight size={20} color="#94a3b8" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

Clinics.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
};
